import { useState } from "react";
import { Link } from "react-router";
import {
  Activity,
  AlertTriangle,
  Bookmark,
  Check,
  Clock,
  Film,
  Globe,
  LifeBuoy,
  Loader2,
  Orbit,
  Quote,
  RefreshCw,
  Target,
  TrendingUp,
  Wand2,
} from "lucide-react";
import { cn } from "@/utils/helpers";
import { canCancelRework, isStuckRework } from "../slotStage";
import { usePermissions } from "@/hooks/usePermissions";
import {
  displayAspectRatio,
  mediaTypeHasDuration,
  SLOT_CONTENT_FINAL_STATUSES,
  type CalendarPlanSlot,
  type CalendarPlatform,
  type AspectRatio,
  type CalendarSlotIdea,
  type IdeaSource,
} from "@/types/contentCalendar.type";
import { formatDuration } from "../calendar.utils";
import { channelWarnings } from "../channelWarnings";
import { PlatformBadges, PlatformPicker } from "./PlatformPicker";
import { ReferenceWell } from "./ReferenceWell";

/**
 * The pitch stage of a post: what the agents researched, the three angles they
 * pitched, and the controls to sign off, replace, or redirect each one.
 */

// ---------------------------------------------------------------- Sources

const SOURCE_ICON = {
  trend: TrendingUp,
  signal: Activity,
  universe: Orbit,
  web: Globe,
  media: Film,
  reference: Bookmark,
} as const;

/**
 * "Where this came from", colour-coded by kind. The colour is a reading aid, not
 * the meaning — the label always names the source and the icon repeats the kind.
 */
const SOURCE_TONE: Record<IdeaSource["type"], string> = {
  trend: "text-co-accent",
  signal: "text-[var(--co-attention)]",
  universe: "text-[var(--co-live)]",
  web: "text-co-muted",
  media: "text-co-muted",
  reference: "text-sky-400/80",
};

export function SourceChip({
  source,
  fullWidth = false,
  rounded = false,
}: {
  source: IdeaSource;
  fullWidth?: boolean;
  /** A pill. The calendar's chips stay square. */
  rounded?: boolean;
}) {
  const Icon = SOURCE_ICON[source.type] ?? Globe;
  const className = cn(
    "items-center gap-1.5 border font-medium text-co-muted transition hover:border-co-accent-border hover:text-co-text",
    rounded
      ? "inline-flex max-w-full rounded-full border-co-line2 bg-co-surface px-3 py-1.5 text-[12.5px]"
      : "rounded-none border-co-line bg-co-surface-solid text-[10.5px]",
    !rounded && (fullWidth ? "flex w-full min-w-0 px-2 py-1" : "inline-flex max-w-[180px] px-2 py-0.5"),
  );
  const body = (
    <>
      <Icon className={cn("size-3 shrink-0", SOURCE_TONE[source.type])} strokeWidth={2} />
      <span className={cn(rounded || fullWidth ? "min-w-0 leading-snug" : "truncate", fullWidth && "flex-1")}>
        {source.label}
      </span>
    </>
  );

  if (source.url) {
    return (
      <a
        href={source.url}
        target="_blank"
        rel="noreferrer"
        title={source.label}
        className={className}
      >
        {body}
      </a>
    );
  }
  if (source.type === "trend") {
    return (
      <Link to="/dashboard/trends" title={source.label} className={className}>
        {body}
      </Link>
    );
  }
  if (source.type === "reference") {
    return (
      <Link to="/dashboard/references" title={source.label} className={className}>
        {body}
      </Link>
    );
  }
  if (source.type === "signal") {
    return (
      <Link to="/dashboard/market-signals" title={source.label} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <span title={source.label} className={className}>
      {body}
    </span>
  );
}

// ---------------------------------------------------------------- Research run

const RESEARCH_SURFACES = [
  "Reading the strategy",
  "Scanning the brand universe",
  "Checking live market signals",
  "Sifting current trends",
  "Searching the web",
] as const;

/**
 * What a research run actually consults, shown live while the agents work.
 * Purely ambient — the run is fire-and-forget and reports back via polling.
 */
export function ResearchTicker() {
  return (
    <div className="flex flex-col gap-3 rounded-none border border-co-accent-border bg-co-accent-dim p-4">
      <div className="flex items-center gap-2.5">
        <Loader2 className="size-4 shrink-0 animate-spin text-co-accent" />
        <p className="text-[13.5px] font-medium text-co-text2">Research run in progress</p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {RESEARCH_SURFACES.map((label, i) => (
          <li
            key={label}
            className="flex items-center gap-2 text-[12px] text-co-muted motion-safe:animate-pulse motion-reduce:animate-none"
            style={{ animationDelay: `${i * 320}ms`, animationDuration: "2.4s" }}
          >
            <span className="size-1 rounded-full bg-co-accent/70" />
            {label}
          </li>
        ))}
      </ul>
      <p className="text-[11.5px] leading-relaxed text-co-faint">
        Takes a few minutes. Safe to close — the post updates itself when the pitch lands.
      </p>
    </div>
  );
}

export function PitchSkeletons() {
  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-3" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex h-full min-h-[168px] flex-col gap-2 rounded-none border border-co-line bg-co-surface p-3.5"
        >
          <div className="h-3.5 w-[55%] animate-pulse rounded bg-co-hover" />
          <div className="h-3 w-full animate-pulse rounded bg-co-surface-solid" />
          <div className="h-3 w-[80%] animate-pulse rounded bg-co-surface-solid" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Card rows

/**
 * A tiny outline of the actual frame. Cheaper to read than the string alone —
 * "9:16" and "3:4" are easy to mix up at 11px, the shapes are not.
 */
function RatioGlyph({ ratio }: { ratio: AspectRatio }) {
  const [w, h] = ratio.split(":").map(Number);
  const long = 14;
  const wide = w >= h;
  const width = wide ? long : Math.round((long * w) / h);
  const height = wide ? Math.round((long * h) / w) : long;
  return (
    <span
      aria-hidden
      className="inline-block shrink-0 border border-co-faint"
      style={{ width, height }}
    />
  );
}

function RowLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-section text-[9.5px] font-semibold uppercase tracking-[0.14em] text-co-faint">
      {children}
    </span>
  );
}

/**
 * Hook options — a set, never a single line. All of them show on the operator
 * card because the pick happens on set; the client card is handed only the first.
 */
function HookOptions({ hooks }: { hooks: string[] }) {
  return (
    <div className="flex min-h-0 flex-col gap-1">
      <RowLabel>Hook options</RowLabel>
      {hooks.length === 0 ? (
        <span className="text-[11px] text-co-faint">None</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {hooks.map((hook, i) => (
            <li
              key={`${hook}-${i}`}
              className="flex gap-1.5 rounded-none border border-co-line bg-co-surface-solid px-2 py-1 text-[11.5px] leading-snug text-co-text2"
            >
              <Quote className="mt-[3px] size-2.5 shrink-0 text-co-faint" strokeWidth={2.4} />
              <span className="min-w-0 flex-1">{hook}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Channels, editable inline — they decide the caption set and the export aspect. */
function ChannelRow({
  channels,
  editable,
  onChange,
  slot,
}: {
  channels: CalendarPlatform[];
  editable: boolean;
  onChange?: (next: CalendarPlatform[]) => void;
  /** The slot's format drives the live compatibility warnings. */
  slot: CalendarPlanSlot;
}) {
  const [open, setOpen] = useState(false);

  const warnings = channelWarnings({
    mediaType: slot.mediaType,
    aspectRatio: displayAspectRatio(slot),
    videoLength: slot.videoLength,
    channels,
  });
  const warningList = warnings.length > 0 && (
    <ul className="flex flex-col gap-0.5">
      {warnings.map((w) => (
        <li
          key={w.platform + w.level}
          className={cn(
            "flex items-start gap-1 text-[10.5px] leading-snug",
            w.level === "HARD" ? "text-red-500" : w.level === "SOFT" ? "text-co-attention" : "text-co-faint",
          )}
        >
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          {w.message}
        </li>
      ))}
    </ul>
  );

  if (editable && open && onChange) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <RowLabel>Channels</RowLabel>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10.5px] text-co-faint transition hover:text-co-muted"
          >
            Done
          </button>
        </div>
        <PlatformPicker value={channels} onChange={onChange} />
        {warningList}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <RowLabel>Channels</RowLabel>
        {editable && onChange && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="text-[10.5px] text-co-faint transition hover:text-co-muted"
          >
            Edit
          </button>
        )}
      </div>
      {channels.length > 0 ? (
        <PlatformBadges platforms={channels} />
      ) : (
        <span className="text-[11px] text-co-faint">Not set</span>
      )}
      {warningList}
    </div>
  );
}

// ---------------------------------------------------------------- Pitch card

function PitchCard({
  slot,
  idea,
  reserveReference,
  onAccept,
  onRegenerate,
  onModify,
  onEdit,
  onCancelRework,
  looksStuck = false,
}: {
  slot: CalendarPlanSlot;
  idea: CalendarSlotIdea;
  reserveReference: boolean;
  onAccept?: () => void;
  onRegenerate?: () => void;
  onModify?: (instruction: string) => void;
  onEdit?: (fields: { channels?: CalendarPlatform[] }) => void;
  /** Present once cancelling is allowed — early, since cancelling is free. */
  onCancelRework?: () => void;
  /** True only once the rework is old enough that we are willing to call it stuck. */
  looksStuck?: boolean;
}) {
  const [redirecting, setRedirecting] = useState(false);
  const [instruction, setInstruction] = useState("");
  const working = idea.status === "WORKING";
  const accepted = idea.status === "ACCEPTED";
  // Whether *this* card can be acted on. Deliberately not a board-wide flag: the
  // backend claims one idea at a time, so a rework here must leave its siblings
  // clickable. Rework and approval are both live in Ideation — the only stage
  // where the three ideas exist and are still the operator's to decide.
  const seatLive = slot.status === "IDEATION";
  const live = !!onRegenerate && seatLive && !working && !accepted;
  const duration = formatDuration(idea.durationSeconds);

  const submitRedirect = () => {
    const trimmed = instruction.trim();
    if (!trimmed || !onModify) return;
    onModify(trimmed);
    setRedirecting(false);
    setInstruction("");
  };

  return (
    <article
      className={cn(
        // Eight direct children map onto the board's subgrid rows so title /
        // hooks / reference / sources / actions line up across the three cards.
        "col-span-1 row-span-8 grid min-w-0 grid-rows-subgrid overflow-hidden rounded-none border p-3.5 transition",
        "bg-co-surface",
        working ? "border-co-accent-border" : "border-co-line hover:border-co-line2",
        working && "opacity-75",
      )}
    >
      <div className="flex flex-wrap items-start gap-2">
        <p className="min-w-0 flex-1 font-crake text-[14px] font-semibold leading-snug tracking-[-0.01em] text-co-text">
          {idea.title}
        </p>
        {working && !looksStuck && (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-co-accent">
            <Loader2 className="size-3 animate-spin" /> Reworking
          </span>
        )}
        {working && looksStuck && (
          <span className="inline-flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--co-attention)]">
            <AlertTriangle className="size-3" strokeWidth={2} /> Stuck
          </span>
        )}
        {/* `decidedAt` is the proof, not the ACCEPTED status: a workshop concept
            idea renders through this card as an accepted one before anybody has
            approved anything, and only earns the badge once it is migrated. */}
        {accepted && !working && idea.decidedAt && (
          <span className="shrink-0 rounded-none border border-co-accent-border bg-co-accent-dim px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-co-accent">
            Selected
          </span>
        )}
      </div>

      <p className="text-[12px] leading-relaxed text-co-muted">{idea.synopsis}</p>

      <HookOptions hooks={idea.hooks} />

      <div className="min-h-0 min-w-0 overflow-hidden">
        <ReferenceWell reference={idea.reference} reserveSpace={reserveReference} />
      </div>

      <div className="flex min-h-0 flex-col gap-1">
        {idea.objectiveClaim ? (
          <>
            <RowLabel>How it delivers the objective</RowLabel>
            <p className="flex gap-1.5 text-[11.5px] leading-relaxed text-co-muted">
              <Target className="mt-[3px] size-3 shrink-0 text-[var(--co-live)]" strokeWidth={2} />
              <span className="min-w-0 flex-1">{idea.objectiveClaim}</span>
            </p>
          </>
        ) : null}
      </div>

      <div className="flex min-h-0 w-full flex-col gap-1">
        {idea.sources.length > 0 ? (
          <>
            <RowLabel>Where this came from</RowLabel>
            <div className="flex w-full flex-col gap-1.5">
              {idea.sources.map((source, i) => (
                <SourceChip key={`${idea.id}-src-${i}`} source={source} fullWidth />
              ))}
            </div>
          </>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-col gap-2.5">
        <ChannelRow
          channels={idea.channels}
          editable={live && !!onEdit}
          onChange={onEdit ? (channels) => onEdit({ channels }) : undefined}
          slot={slot}
        />

        <div className="flex flex-col gap-1">
          <RowLabel>Aspect ratio</RowLabel>
          <span className="inline-flex w-fit items-center gap-1.5 text-[11.5px] text-co-text2">
            <RatioGlyph ratio={idea.aspectRatio} />
            {idea.aspectRatio}
          </span>
        </div>

        {mediaTypeHasDuration(slot.mediaType) && duration ? (
          <div className="flex flex-col gap-1">
            <RowLabel>Duration</RowLabel>
            <span className="inline-flex w-fit items-center gap-1.5 text-[11.5px] text-co-text2">
              <Clock className="size-3 text-co-faint" strokeWidth={2} />
              {duration}
              {slot.videoLength && (
                <span className="text-co-faint">
                  · {slot.videoLength === "LONG_FORM" ? "long form" : "short form"}
                </span>
              )}
            </span>
          </div>
        ) : null}

        {accepted && idea.decidedAt ? (
          <p className="text-[10.5px] text-co-faint">
            Selected {new Date(idea.decidedAt).toLocaleDateString()}
          </p>
        ) : null}
      </div>

      {redirecting ? (
        <div className="flex flex-col gap-2 self-end">
          <input
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitRedirect()}
            maxLength={600}
            autoFocus
            placeholder="e.g. Same format, but tie it to Ramadan"
            className="h-8 min-w-0 w-full rounded-none border border-co-line bg-co-surface-solid px-2.5 text-[12px] text-co-text placeholder:text-co-faint focus:border-b-co-accent-border focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!live || !instruction.trim()}
              onClick={submitRedirect}
              className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-none bg-co-accent px-2.5 text-[12px] font-semibold text-co-on-accent transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Wand2 className="size-3.5" /> Apply
            </button>
            <button
              type="button"
              onClick={() => setRedirecting(false)}
              className="h-8 shrink-0 rounded-none border border-co-line px-2.5 text-[12px] text-co-muted transition hover:bg-co-hover hover:text-co-text"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : onCancelRework ? (
        // A rework claims the IDEA row, so nothing at slot level can free it —
        // without this the card spins forever and can never be approved. The
        // three normal buttons are all disabled while WORKING anyway, so this
        // takes their place rather than crowding in beside them.
        <div className="flex flex-col gap-1.5 self-end">
          <p className="text-[11px] leading-relaxed text-co-muted">
            {looksStuck
              ? "This rework stopped responding. Cancelling brings the idea back exactly as it was."
              : "Changed your mind? Cancelling brings the idea back exactly as it was."}
          </p>
          <button
            type="button"
            onClick={onCancelRework}
            title="Free this card — its previous content is restored"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-none bg-co-accent px-2.5 py-1.5 text-[12px] font-semibold text-co-on-accent transition hover:opacity-90"
          >
            <LifeBuoy className="size-3.5" /> Cancel rework
          </button>
        </div>
      ) : onRegenerate ? (
        <div className="flex flex-col gap-1.5 self-end">
          <button
            type="button"
            disabled={!live}
            onClick={onRegenerate}
            title={working ? "Reworking this angle…" : "Replace with a different angle"}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-none border border-co-line px-2.5 py-1.5 text-[12px] font-medium text-co-muted transition hover:bg-co-hover hover:text-co-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RefreshCw className="size-3.5" /> New angle
          </button>
          <button
            type="button"
            disabled={!live}
            onClick={() => setRedirecting(true)}
            title={working ? "Reworking this angle…" : "Steer this pitch with an instruction"}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-none border border-co-line px-2.5 py-1.5 text-[12px] font-medium text-co-muted transition hover:bg-co-hover hover:text-co-text disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Wand2 className="size-3.5" /> Redirect
          </button>
          {onAccept && (
            <button
              type="button"
              disabled={!live}
              onClick={onAccept}
              title="Choose this concept and send it to the client proposal page"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-none bg-co-accent-fill px-2.5 py-1.5 text-[12px] font-semibold text-co-on-accent transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Check className="size-3.5" />
              Choose concept
            </button>
          )}
        </div>
      ) : (
        <div />
      )}
    </article>
  );
}

// ---------------------------------------------------------------- Board

export interface PitchBoardActions {
  onAccept: (ideaId: string) => void;
  onRegenerate: (ideaId: string) => void;
  onModify: (ideaId: string, instruction: string) => void;
  onEditIdea: (ideaId: string, fields: { channels?: CalendarPlatform[] }) => void;
  onRepitchAll: () => void;
  /** Free a card frozen mid-rework. Absent on read-only boards. */
  onCancelRework?: (ideaId: string) => void;
}

export default function PitchBoard({
  slot,
  actions,
  now,
}: {
  slot: CalendarPlanSlot;
  actions?: PitchBoardActions;
  /**
   * A ticking clock from the parent, when it wants stuck reworks surfaced.
   * Omitted (the default) reproduces the old behaviour: a WORKING card just
   * spins.
   */
  now?: number;
}) {
  const { canEdit } = usePermissions();
  const allIdeas = [...(slot.ideas ?? [])].sort((a, b) => a.order - b.order);
  const writeActions = canEdit ? actions : undefined;
  // Every state from sign-off on, not SIGNED_OFF alone: a post with the client,
  // or approved by them, has had its pick made just as much as one that has only
  // been signed off. Keying on the single status put the three losing ideas back
  // on screen under an "Ideation" header the moment a post was approved.
  const decided = (SLOT_CONTENT_FINAL_STATUSES as string[]).includes(slot.status);
  // Once one idea is approved, the board is just that post — siblings stay in
  // the data model but leave the UI so the workroom reads as a single pick.
  const ideas = decided
    ? allIdeas.filter((idea) => idea.status === "ACCEPTED")
    : allIdeas;
  // Re-pitching wipes every idea, which would pull the rug from under a rework
  // that is mid-flight. The only board-wide gate — and it names its reason.
  //
  // A rework that has stopped responding does NOT hold this gate shut, or one
  // dead run would make the whole board unusable: the wipe is exactly what the
  // operator wants at that point, and cancelling first would be busywork.
  const reworking = allIdeas.some(
    (idea) => idea.status === "WORKING" && !(now !== undefined && isStuckRework(idea, now)),
  );
  const reserveReference = ideas.some((idea) => !!idea.reference);
  return (
    <div className="flex flex-col gap-3">
      {!decided ? (
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="font-section text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--co-attention)]">
              Ideation
            </p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-co-muted">
              Three ways to execute this content plan. Rework them until one is right, then choose
              it — that sends the selected concept to the client proposal page.
            </p>
          </div>
          {writeActions ? (
            <button
              type="button"
              disabled={reworking}
              onClick={writeActions.onRepitchAll}
              title={
                reworking
                  ? "Wait for the angle being reworked to land"
                  : "Research and pitch three new ideas"
              }
              className="inline-flex shrink-0 items-center gap-1.5 rounded-none border border-co-line px-2.5 py-1.5 text-[11.5px] font-medium text-co-muted transition hover:bg-co-hover hover:text-co-text disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RefreshCw className="size-3" /> Re-pitch all
            </button>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          // Explicit 8 tracks so each card's `grid-rows-subgrid` can adopt them;
          // without this, tall reference videos size outside the card and spill.
          "grid min-w-0 grid-rows-[repeat(8,auto)] gap-x-3 gap-y-2.5",
          decided ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-3",
        )}
      >
        {ideas.map((idea) => (
          <PitchCard
            key={idea.id}
            slot={slot}
            idea={idea}
            reserveReference={reserveReference}
            onAccept={writeActions ? () => writeActions.onAccept(idea.id) : undefined}
            onRegenerate={writeActions ? () => writeActions.onRegenerate(idea.id) : undefined}
            onModify={
              writeActions ? (instruction) => writeActions.onModify(idea.id, instruction) : undefined
            }
            onEdit={writeActions ? (fields) => writeActions.onEditIdea(idea.id, fields) : undefined}
            onCancelRework={
              // The early floor, not the strict one: cancelling is free and
              // restores the card's own content, so there is no reason to make
              // someone wait for permission to undo a rework they started.
              writeActions?.onCancelRework && now !== undefined && canCancelRework(idea, now)
                ? () => writeActions.onCancelRework?.(idea.id)
                : undefined
            }
            looksStuck={now !== undefined && isStuckRework(idea, now)}
          />
        ))}
      </div>
    </div>
  );
}
